import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { TagIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';
import { DayItemProps, TaskRecord } from '../types';
import { fetchDayRecords } from '../utils';
import DayRecordsTable from './DayRecordsTable';

const DayItem = memo(({ day, maxIncome }: DayItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dayRecords, setDayRecords] = useState<TaskRecord[]>([]);
  const progressPercentage = (day.income / maxIncome) * 100;

  // Fetch records when popover opens
  const handleFetchRecords = useCallback(async () => {
    if (!isOpen) return;
    const records = await fetchDayRecords(day.fullDate);
    setDayRecords(records);
  }, [isOpen, day.fullDate]);

  useEffect(() => {
    if (isOpen) {
      handleFetchRecords();
    }
  }, [isOpen, handleFetchRecords]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-4 py-4 cursor-pointer hover:bg-gray-200/60 transition-colors duration-200 rounded-lg px-2">
          {/* Date Display */}
          <div className="flex-shrink-0">
            <div className="w-14 h-14 bg-gray-200 rounded-lg p-1 text-center">
              <div className="text-sm font-semibold text-gray-600">{day.day}</div>
              <div className="text-2xl font-semibold">{day.date}</div>
            </div>
          </div>
          <div className="text-sm sm:w-40 text-left text-gray-500">{day.fullDate}</div>

          {/* Income Progress Bar */}
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-2">
              <TagIcon />
              <span className="text-xl font-semibold">
                {day.income.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
            <div className="text-sm text-gray-500 mt-1">{progressPercentage.toFixed(2)}%</div>
          </div>

          {/* Daily Stats */}
          <div className="sm:w-32 flex-shrink-0 text-right">
            <div className="text-sm text-gray-600">Supply: {day.supplies}</div>
            <div className="text-sm text-gray-600">Turn: {day.credits}</div>
          </div>
        </div>
      </PopoverTrigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className="w-[370px] z-50 border border-gray-200 bg-white rounded-md shadow-md">
          <DayRecordsTable records={dayRecords} date={day.fullDate} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  );
});

DayItem.displayName = 'DayItem';
export default DayItem;
